/**
 * Media fingerprinting:
 * - collectMediaDevices: single enumerateDevices() + summary (browser.other.media).
 * - getAudioFormats / getVideoFormats: HTMLMediaElement.canPlayType → media.audioFormats / media.videoFormats.
 */

const MEDIA_ENUMERATE_DEVICES_MS = 3000;

/**
 * @returns {{ keys: string[], count: number | null }}
 */
function readSupportedConstraints() {
    try {
        const md = navigator.mediaDevices;
        if (!md || typeof md.getSupportedConstraints !== 'function') {
            return { keys: [], count: null };
        }
        const c = md.getSupportedConstraints();
        if (!c || typeof c !== 'object') {
            return { keys: [], count: null };
        }
        const keys = Object.keys(c).filter((k) => c[k]);
        keys.sort();
        return { keys, count: keys.length };
    } catch {
        return { keys: [], count: null };
    }
}

function enumerateDevicesWithTimeout() {
    let timeoutId;
    const timeoutPromise = new Promise((resolve) => {
        timeoutId = setTimeout(() => {
            resolve({ status: 'timeout', devices: [] });
        }, MEDIA_ENUMERATE_DEVICES_MS);
    });
    return Promise.race([
        navigator.mediaDevices.enumerateDevices().then((devices) => ({
            status: 'ok',
            devices,
        })),
        timeoutPromise,
    ]).finally(() => clearTimeout(timeoutId));
}

/**
 * @returns {Promise<{ devices: Array<{ kind: string, label: string, deviceId: string, groupId: string }>, summary: Record<string, unknown> }>}
 */
export async function collectMediaDevices() {
    const { keys: supportedConstraints, count: supportedConstraintsCount } = readSupportedConstraints();

    const baseSummary = (enumerateStatus, counts) => ({
        enumerateStatus,
        audioInputCount: counts.audioInputCount,
        videoInputCount: counts.videoInputCount,
        audioOutputCount: counts.audioOutputCount,
        totalDeviceCount: counts.totalDeviceCount,
        labelsNonEmptyCount: counts.labelsNonEmptyCount,
        supportedConstraintsCount,
        supportedConstraints,
    });

    if (!window.MediaDevices || typeof navigator.mediaDevices?.enumerateDevices !== 'function') {
        return {
            devices: [],
            summary: baseSummary('unsupported', {
                audioInputCount: 0,
                videoInputCount: 0,
                audioOutputCount: 0,
                totalDeviceCount: 0,
                labelsNonEmptyCount: 0,
            }),
        };
    }

    try {
        const { status, devices } = await enumerateDevicesWithTimeout();
        if (status === 'timeout') {
            return {
                devices: [],
                summary: baseSummary('timeout', {
                    audioInputCount: 0,
                    videoInputCount: 0,
                    audioOutputCount: 0,
                    totalDeviceCount: 0,
                    labelsNonEmptyCount: 0,
                }),
            };
        }
        let audioInputCount = 0;
        let videoInputCount = 0;
        let audioOutputCount = 0;
        let labelsNonEmptyCount = 0;
        for (let i = 0; i < devices.length; i++) {
            const d = devices[i];
            if (d.kind === 'audioinput') {
                audioInputCount++;
            } else if (d.kind === 'videoinput') {
                videoInputCount++;
            } else if (d.kind === 'audiooutput') {
                audioOutputCount++;
            }
            if (d.label && String(d.label).trim().length > 0) {
                labelsNonEmptyCount++;
            }
        }
        const mapped = devices.map((device) => ({
            kind: device.kind,
            label: device.label,
            deviceId: device.deviceId,
            groupId: device.groupId,
        }));
        return {
            devices: mapped,
            summary: baseSummary('ok', {
                audioInputCount,
                videoInputCount,
                audioOutputCount,
                totalDeviceCount: devices.length,
                labelsNonEmptyCount,
            }),
        };
    } catch (err) {
        return {
            devices: [],
            summary: baseSummary('error', {
                audioInputCount: 0,
                videoInputCount: 0,
                audioOutputCount: 0,
                totalDeviceCount: 0,
                labelsNonEmptyCount: 0,
            }),
        };
    }
}


export async function getAudioFormats() {
    var audioFormats = {};
    try {
        var formatNames = ["audio/3gpp", "audio/3gpp2", "audio/AMR-NB", "audio/AMR-WB", "audio/GSM", "audio/aac", "audio/basic", "audio/flac", "audio/midi", "audio/mpeg", 'audio/mp4; codecs="mp4a.40.2"', 'audio/mp4; codecs="ac-3"', 'audio/mp4; codecs="ec-3"', 'audio/ogg; codecs="flac"', 'audio/ogg; codecs="vorbis"', 'audio/ogg; codecs="opus"', 'audio/wav; codecs="1"', 'audio/webm; codecs="vorbis"', 'audio/webm; codecs="opus"', "audio/x-aiff", "audio/x-mpegurl"];
        var element = document.createElement("audio");
        formatNames.forEach(function (format) {
            let res = !!element.canPlayType && element.canPlayType(format);
            if (res) audioFormats[format] = res;
        });

        element.remove();
    } catch (e) {
        return null;
    }
    return audioFormats;
}

export async function getVideoFormats() {
    var videoFormats = {};
    try {
        var formatNames = [
            'video/mp4; codecs="avc1.42E01E"',
            'video/mp4; codecs="avc1.640028"',
            'video/mp4; codecs="hev1.1.6.L93.B0"',
            'video/mp4; codecs="hvc1.1.6.L93.B0"',
            'video/mp4; codecs="av01.0.05M.08"',
            'video/webm; codecs="vp09.00.10.08"',
            'video/webm; codecs="vp8"',
            'video/ogg; codecs="theora"',
        ];
        var element = document.createElement("video");
        formatNames.forEach(function (format) {
            let res = !!element.canPlayType && element.canPlayType(format);
            if (res) videoFormats[format] = res;
        });
        element.remove();
    } catch (e) {
        return null;
    }
    return videoFormats;
}
